import Head from 'react-helmet';
import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import { getFaqSchemaTemplate } from 'app/shared/modules/FaqSection/controller';
import schemaUtils from 'app/shared/utils/schemaUtils';
import { answerBody, answerList, answerListHeader, caret, container, faqItem, faqSummary } from './faqModule.styles';

type FaqAnswerType = 'answer' | 'unorderedList' | 'orderedList';

type FaqQuestion = {
  question: string;
  answer: string | Array<string>;
  answerType?: FaqAnswerType;
  listHeader?: string;
};

type FaqTemplate = {
  data: Array<FaqQuestion>;
  props: Record<string, unknown>;
};

const renderAnswer = (question: FaqQuestion) => {
  const type = question.answerType ?? 'answer';

  if (type === 'answer') {
    return <p className={answerBody}>{question.answer as string}</p>;
  }

  const items = Array.isArray(question.answer) ? question.answer : [];
  const ListTag = type === 'unorderedList' ? 'ul' : 'ol';

  return (
    <>
      {question.listHeader && <p className={answerListHeader}>{question.listHeader}</p>}
      <ListTag className={answerList}>
        {items.map((item, idx) => (
          <li key={`faq-list-item-${idx}`}>{item}</li>
        ))}
      </ListTag>
    </>
  );
};

const buildJsonLd = (questions: Array<FaqQuestion>) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: questions.map((q) => ({
    '@type': 'Question',
    name: q.question,
    acceptedAnswer: schemaUtils.getFormattedFaqAnswer(q.answer, q.answerType ?? 'answer', q.listHeader ?? null),
  })),
});

export const FaqModule = () => {
  const listing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);

  if (!listing || listing.active === false) {
    return null;
  }

  const template = getFaqSchemaTemplate({
    searchSlug: undefined,
    data: { listing },
  }) as FaqTemplate | null;
  const questions = template?.data;

  if (!questions || questions.length === 0) {
    return null;
  }

  return (
    <>
      <Head
        script={[
          {
            type: 'application/ld+json',
            innerHTML: JSON.stringify(buildJsonLd(questions)),
          },
        ]}
      />
      <div className={container}>
        {questions.map((q) => (
          <details key={`faq-${q.question}`} className={faqItem}>
            <summary className={faqSummary}>
              <span>{q.question}</span>
              <svg className={caret} width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <path
                  d="M4 6L8 10L12 6"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </summary>
            {renderAnswer(q)}
          </details>
        ))}
      </div>
    </>
  );
};
